import type { SummaryPageContext } from '@shared/types/pageContext';
import { normalizeNilUuid } from '@shared/utils';
import type { ArtifactGetResponse } from '../api/artifactApi';
import { findMostRecentSessionDate } from './shareSessionDate';
import { isArtifactTranscriptSourceType } from './sourceTypes';

const DOCUMENT_SOURCE_TYPES = new Set(['google_doc', 'document', 'drive_file']);

type ArtifactSource = ArtifactGetResponse['sources'][number];
type ArtifactTranscriptSession = NonNullable<ArtifactGetResponse['transcript_sessions']>[number];

const normalize = (value: string | null | undefined): string => value?.trim() ?? '';

function collectDocumentIDs(sources: ArtifactSource[]): string[] | undefined {
  const seen = new Set<string>();
  const documentIds: string[] = [];

  for (const source of sources) {
    const type = normalize(source?.type);
    const value = normalize(source?.value);
    if (!DOCUMENT_SOURCE_TYPES.has(type) || value === '' || seen.has(value)) {
      continue;
    }

    seen.add(value);
    documentIds.push(value);
  }

  return documentIds.length > 0 ? documentIds : undefined;
}

function collectTranscriptEntries(
  sources: ArtifactSource[]
): Array<{ transcriptId: string; transcriptName: string | null }> {
  const seen = new Set<string>();
  const transcriptEntries: Array<{ transcriptId: string; transcriptName: string | null }> = [];

  for (const source of sources) {
    const type = normalize(source?.type);
    const value = normalize(source?.value);
    if (!isArtifactTranscriptSourceType(type) || value === '' || seen.has(value)) {
      continue;
    }

    seen.add(value);
    const transcriptName = normalize(source?.label);

    transcriptEntries.push({
      transcriptId: value,
      transcriptName: transcriptName === '' ? null : transcriptName,
    });
  }

  return transcriptEntries;
}

/**
 * Collects the artifact's transcript and document sources with their real types.
 *
 * This is what lets the backend rebuild an artifact's sources without inferring a type
 * from a bare ID. See `transcriptMetadata.sources` in `SummaryPageContext` and
 * `extractSources` in `backend/pkg/artifact/metadata_extractor.go`.
 */
function collectContextSources(
  sources: ArtifactSource[]
): NonNullable<NonNullable<SummaryPageContext['transcriptMetadata']>['sources']> {
  const seen = new Set<string>();
  const contextSources: NonNullable<
    NonNullable<SummaryPageContext['transcriptMetadata']>['sources']
  > = [];

  for (const source of sources) {
    const type = normalize(source?.type);
    const value = normalize(source?.value);
    if (
      value === '' ||
      (!isArtifactTranscriptSourceType(type) && !DOCUMENT_SOURCE_TYPES.has(type))
    ) {
      continue;
    }

    const key = `${type}\u0000${value}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);

    const label = normalize(source?.label);
    contextSources.push({ type, value, ...(label === '' ? {} : { label }) });
  }

  return contextSources;
}

function collectTranscriptDateByID(
  transcriptSessions: ArtifactTranscriptSession[] | null | undefined
): Map<string, string> {
  const transcriptDateByID = new Map<string, string>();
  if (!Array.isArray(transcriptSessions) || transcriptSessions.length === 0) {
    return transcriptDateByID;
  }

  for (const session of transcriptSessions) {
    const transcriptID = normalize(session?.transcript_id);
    if (transcriptID === '' || transcriptDateByID.has(transcriptID)) {
      continue;
    }
    const transcriptSessionDate = normalize(session?.session_start_at);
    if (transcriptSessionDate === '') {
      continue;
    }
    transcriptDateByID.set(transcriptID, transcriptSessionDate);
  }

  return transcriptDateByID;
}

function resolveMostRecentSessionDate(sessionDates: string[] | null | undefined): string {
  return findMostRecentSessionDate(sessionDates) ?? '';
}

function toTranscriptMetadata(
  sources: ArtifactSource[],
  subjectID: string | null | undefined,
  transcriptSessions: ArtifactTranscriptSession[] | null | undefined
): SummaryPageContext['transcriptMetadata'] | undefined {
  const transcriptEntries = collectTranscriptEntries(sources);
  if (transcriptEntries.length === 0) {
    return undefined;
  }

  const transcriptDateByID = collectTranscriptDateByID(transcriptSessions);
  const mostRecentSessionDate = resolveMostRecentSessionDate(
    transcriptSessions?.map((session) => session?.session_start_at ?? '')
  );
  const canFallbackSingleTranscript =
    transcriptEntries.length === 1 && mostRecentSessionDate !== '';

  const transcriptIds = transcriptEntries.map((entry) => entry.transcriptId);
  const transcriptNames = transcriptEntries.map((entry) => entry.transcriptName ?? '');
  const transcriptDates = transcriptEntries.map((entry) => {
    const alignedTranscriptDate = transcriptDateByID.get(entry.transcriptId);
    if (alignedTranscriptDate !== undefined) {
      return alignedTranscriptDate;
    }
    if (canFallbackSingleTranscript) {
      return mostRecentSessionDate;
    }
    return '';
  });
  const hasTranscriptName = transcriptNames.some((name) => name !== '');

  const contextSources = collectContextSources(sources);

  return {
    transcriptIds,
    transcriptDates,
    ...(hasTranscriptName ? { transcriptNames } : {}),
    subjectId: normalizeNilUuid(subjectID) ?? '',
    documentCount: transcriptIds.length,
    ...(contextSources.length > 0 ? { sources: contextSources } : {}),
  };
}

export function toSummaryPageContextFromArtifact(
  artifact: ArtifactGetResponse
): SummaryPageContext {
  const sources = Array.isArray(artifact.sources) ? artifact.sources : [];
  const documentIds = collectDocumentIDs(sources);
  const transcriptMetadata = toTranscriptMetadata(
    sources,
    artifact.subject_id,
    artifact.transcript_sessions
  );

  return {
    pageType: 'summary',
    ...(documentIds ? { documentIds } : {}),
    ...(transcriptMetadata ? { transcriptMetadata } : {}),
    currentArtifact: {
      artifactId: artifact.id,
      revision: artifact.revision,
      profileSlug: artifact.profile_slug,
    },
    timestamp: Date.now(),
  };
}
